import React, { useState, useEffect } from 'react';
import { Phone, Menu, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSiteImage } from '../hooks/useContent';
import StorageImage from './StorageImage';

const NAV_LINKS = [
  { label: 'Services', href: '#services' },
  { label: 'Railings, Partitions & Showers', href: '#systems' },
  { label: 'Hardware & Glass Sizes', href: '#hardware' },
  { label: 'Gallery', href: '#gallery' },
  { label: 'Contact', href: '#contact' },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { data: logoImage } = useSiteImage('logo');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  return (
    <>
      <nav className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'glass-bg shadow-xl' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link
              to="/"
              onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); setIsOpen(false); }}
              className="flex items-center gap-3 group"
            >
              <div className="relative w-12 h-12 rounded-xl overflow-hidden border border-white/10 flex-shrink-0">
                <StorageImage
                  path={logoImage?.image_path || ''}
                  alt="Primeview Glass Logo"
                  className="w-full h-full object-cover object-center transition-transform duration-300 group-hover:scale-105"
                />
              </div>
              <div>
                <span className="font-display text-xl font-bold text-white tracking-tight leading-none block">
                  Primeview Glass
                </span>
                <span className="text-[10px] text-gold-400 font-semibold tracking-[0.18em] uppercase block mt-0.5">
                  Transparency Meets Style
                </span>
              </div>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-6">
              {NAV_LINKS.map(({ label, href }) => (
                <a key={label} href={href} className="nav-link">{label}</a>
              ))}
            </div>

            {/* Desktop CTAs + mobile hamburger */}
            <div className="flex items-center gap-3">
              <div className="hidden lg:flex flex-col items-end gap-1">
                <a
                  href="tel:3014335323"
                  className="flex items-center gap-1.5 bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold px-4 py-1.5 rounded-xl transition-all duration-200 hover:shadow-glow"
                >
                  <Phone className="w-3.5 h-3.5" />
                  (301) 433-5323
                </a>
                <a
                  href="tel:6723995323"
                  className="flex items-center gap-1.5 text-white/70 hover:text-white/90 text-xs font-medium px-4 py-0.5 transition-colors"
                >
                  <Phone className="w-3 h-3" />
                  (672) 399-5323
                </a>
              </div>

              {/* Hamburger — shown when closed */}
              {!isOpen && (
                <button
                  onClick={() => setIsOpen(true)}
                  className="md:hidden text-white/80 hover:text-white transition-colors p-1"
                  aria-label="Open menu"
                >
                  <Menu className="w-6 h-6" />
                </button>
              )}

              {/* X — shown when open */}
              {isOpen && (
                <button
                  onClick={() => setIsOpen(false)}
                  className="md:hidden text-white/80 hover:text-white transition-colors p-1"
                  aria-label="Close menu"
                >
                  <X className="w-6 h-6" />
                </button>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile menu — conditionally rendered, no animation interference */}
      {isOpen && (
        <>
          {/* Backdrop — clicking outside closes menu */}
          <div
            className="fixed inset-0 z-40 bg-slate-950/70 backdrop-blur-sm md:hidden"
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />

          {/* Menu panel */}
          <div
            className="fixed top-20 left-0 right-0 z-50 md:hidden px-3"
          >
            <div
              className="rounded-2xl overflow-hidden"
              style={{
                background: 'rgba(10,14,30,0.97)',
                backdropFilter: 'blur(20px)',
                boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
                border: '1px solid rgba(255,255,255,0.08)',
              }}
            >
              <div className="flex flex-col p-4 gap-1">
                {NAV_LINKS.map(({ label, href }) => (
                  <a
                    key={label}
                    href={href}
                    onClick={() => setIsOpen(false)}
                    className="text-slate-200 hover:text-white hover:bg-white/10 px-4 py-3.5 rounded-xl text-base font-medium transition-all duration-150"
                  >
                    {label}
                  </a>
                ))}
                <div className="flex flex-col gap-2 pt-3 mt-1 border-t border-white/10">
                  <a
                    href="tel:3014335323"
                    onClick={() => setIsOpen(false)}
                    className="flex items-center gap-2 text-white bg-brand-600 hover:bg-brand-700 px-4 py-3 rounded-xl font-semibold text-sm transition-all"
                  >
                    <Phone className="w-4 h-4" /> (301) 433-5323
                  </a>
                  <a
                    href="tel:6723995323"
                    onClick={() => setIsOpen(false)}
                    className="flex items-center gap-2 text-white/80 hover:text-white bg-white/5 hover:bg-white/10 px-4 py-3 rounded-xl font-medium text-sm transition-all"
                  >
                    <Phone className="w-4 h-4" /> (672) 399-5323
                  </a>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Navbar;
